import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Release } from '../lib/api';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { CheckCircle2, Wine, Music2, Loader2 } from 'lucide-react';

export default function ReleaseForm() {
  const { guestId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(false);

  const [form, setForm] = useState({
    fullName: '', email: '', company: '',
    instagram: '', twitter: '', linkedin: '', facebook: '',
    phone: '', signature: '', notes: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Release.get(guestId)
      .then((d) => {
        setData(d);
        setForm(f => ({
          ...f,
          fullName: d.guest.name || '',
          email: d.guest.email || ''
        }));
        if (d.guest.releaseStatus === 'signed') setSubmitted(true);
      })
      .catch(() => setLoadErr(true));
  }, [guestId]);

  if (loadErr) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF7F2]">
        <div className="text-center">
          <div className="text-stone-500">Release form not found</div>
          <Button variant="outline" className="mt-3" onClick={() => navigate('/')}>Back home</Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF7F2]">
        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
      </div>
    );
  }

  const { guest, show } = data;
  const Icon = show.id === 'wine-talks' ? Wine : Music2;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.signature.trim() || !form.fullName.trim() || !form.email.trim()) return;
    setSubmitting(true);
    try {
      await Release.submit(guestId, {
        fullName: form.fullName,
        email: form.email,
        signature: form.signature,
        notes: form.notes,
        company: form.company,
        instagram: form.instagram,
        twitter: form.twitter,
        linkedin: form.linkedin,
        facebook: form.facebook,
        phone: form.phone,
        accepted: true
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF7F2] p-6">
        <div className="max-w-md w-full bg-white rounded-xl border border-stone-200 p-8 text-center shadow-sm">
          <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-4" style={{ background: `${show.color}1a`, color: show.color }}>
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h2 className="text-[20px] font-serif text-stone-900">Thank you, {(guest.name || form.fullName).split(' ')[0]}</h2>
          <p className="text-stone-600 text-[13.5px] mt-1.5">Your release has been recorded for &ldquo;{guest.episodeTitle || 'your episode'}&rdquo; on {show.name}.</p>
        </div>
      </div>
    );
  }

  const clauses = data.clauses || [];

  return (
    <div className="min-h-screen bg-[#FAF7F2] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-5">
          <div className="h-10 w-10 rounded-md grid place-items-center text-white" style={{ background: show.color }}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[14px] font-semibold text-stone-900">{show.name}</div>
            <div className="text-[11.5px] text-stone-500">Podcast Form</div>
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-7 shadow-sm">
          <h1 className="text-[26px] font-serif text-stone-900 mb-1">{show.id === 'wine-talks' ? 'WineTalks' : show.name} Podcast Form</h1>
          <p className="text-[13.5px] text-stone-600 mb-6" data-testid="release-intro">{data.intro}</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[13px] text-stone-700">Name</Label>
              <Input value={form.fullName} onChange={(e) => set('fullName', e.target.value)} required />
            </div>
            <div>
              <Label className="text-[13px] text-stone-700">Email Address</Label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required />
            </div>
            <div>
              <Label className="text-[13px] text-stone-700">Company</Label>
              <Input value={form.company} onChange={(e) => set('company', e.target.value)} />
            </div>
            <div>
              <Label className="text-[13px] text-stone-700">Instagram Handle</Label>
              <Input value={form.instagram} onChange={(e) => set('instagram', e.target.value)} placeholder="@" />
            </div>
            <div>
              <Label className="text-[13px] text-stone-700">Twitter Handle</Label>
              <Input value={form.twitter} onChange={(e) => set('twitter', e.target.value)} placeholder="@" />
            </div>
            <div>
              <Label className="text-[13px] text-stone-700">LinkedIn Handle</Label>
              <Input value={form.linkedin} onChange={(e) => set('linkedin', e.target.value)} />
            </div>
            <div>
              <Label className="text-[13px] text-stone-700">Facebook Handle</Label>
              <Input value={form.facebook} onChange={(e) => set('facebook', e.target.value)} />
            </div>
            <div>
              <Label className="text-[13px] text-stone-700">Phone Number</Label>
              <Input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </div>

            <div className="pt-3">
              <Label className="text-[14px] text-stone-900 font-semibold">
                Release Form <span className="text-red-500">*</span>
              </Label>
              <div className="mt-3 space-y-4 text-[13.5px] leading-relaxed text-stone-700 bg-stone-50 rounded-md p-5 border border-stone-200" data-testid="release-clauses">
                {clauses.map((c, i) => (
                  <p key={`clause-${i}-${(c || '').slice(0, 24)}`}>{c}</p>
                ))}
              </div>
            </div>

            <div className="pt-2">
              <div className="text-[13px] text-stone-700 mb-1.5">By typing (signing) your name below, you are agreeing to the above terms and conditions.</div>
              <Input
                value={form.signature}
                onChange={(e) => set('signature', e.target.value)}
                placeholder="Type your name to sign"
                className="font-serif italic text-[18px] h-12"
                required
              />
            </div>

            <div className="pt-2">
              <Label className="text-[13px] text-stone-700">Additional notes (optional)</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </div>

            <div className="pt-3 flex items-center gap-3">
              <Button
                type="submit"
                disabled={submitting || !form.signature.trim() || !form.fullName.trim() || !form.email.trim()}
                className="text-white px-6"
                style={{ background: show.color }}
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </Button>
              <button
                type="button"
                onClick={() => setForm({ fullName: '', email: '', company: '', instagram: '', twitter: '', linkedin: '', facebook: '', phone: '', signature: '', notes: '' })}
                className="text-[13px] text-stone-500 hover:text-stone-800"
              >
                Clear form
              </button>
            </div>
          </form>
        </div>

        <div className="text-center mt-4 text-[11px] text-stone-400">
          Powered by {show.name}
        </div>
      </div>
    </div>
  );
}
